import { CommonModule, Location } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { TranslateService } from '@ngx-translate/core';
import { AccountInformationScopesComponent } from '../account-information/account-information-scopes.component';
import { DataFieldComponent } from '../../data';

import { clone, cloneDeep } from 'lodash-es';

import {
    ActionDef,
    ActionItemDef,
    AnyKey,
    EditorBaseComponent,
    ChangedEvent,
    ContextRoleService,
    MetaService,
    OptionDTO,
    ReadContextRoleScopeProfilesRequestDTO,
    ReadMetaRequestDTO,
    TypeScopeDTO,
    UserAccountInformationDTO,
    UserContextRoleDTO,
    UserContextRoleScopeDTO,
    UserContextRoleScopeFieldDTO,
    doReadContextRoleScope,
    firstOrDefault,
    isAssigned,
    updateFields,
    GlobalsService,
    getDeletedScopes,
    clearAllInTypeScopes,
    userContextRoleScopesToTypeScopes,
    isEmpty,
} from '../../data';
import { ContextRoleScopePickerKind, showScopePicker } from '../context-role-scope-picker/context-role-scope-picker.component';

export interface ContextRoleChangedEvent extends ChangedEvent {
    contextCd: string;
    roleCd: string;
    preferedLanguageCd: string;
    grantedFrom?: Date;
    grantedTo?: Date;
    scopeFields: UserContextRoleScopeFieldDTO[];
    existingScopes: UserContextRoleScopeDTO[];
    newScopes: UserContextRoleScopeDTO[];
    deletedScopes: UserContextRoleScopeDTO[];
    hasDeletedScopes: boolean;
}

const ComponentName = 'user-context-role-details-editor';

@Component({
    selector: `app-${ComponentName}`,
    templateUrl: `./user-context-role-details-editor.component.html`,
    styleUrls: [`./user-context-role-details-editor.component.scss`],
    standalone: true,
    imports: [CommonModule, FormsModule, CalendarModule, CheckboxModule, SelectModule, TranslateModule, AccountInformationScopesComponent, DataFieldComponent],
})
export class UserContextRoleDetailsEditorComponent extends EditorBaseComponent {
    private _showAddScopeAction = false;
    private _newScopeItemActions: ActionItemDef[] = [
        {
            getIcon: (element) => (element.isDeleted === true ? 'pi pi-undo' : 'pi pi-times'),
            styleClass: 'danger',
            tooltip: (element) => (element.isDeleted === true ? this.scopesDeleteUndoButton : this.scopesDeleteButton),
            isVisible: (element) => element?.canDeleteScopeRowAction === true && this._newScopeFieldsTextC,
            run: (element) => this.onRemoveNewScope(element),
        },
    ];
    private _newScopeFooterActions: ActionDef[] = [
        {
            icon: 'pi pi-plus',
            styleClass: 'successs',
            tooltip: () => this.newScopesAddButton,
            isVisible: () => this.isNewRole || this.showAddScopeAction,
            run: () => this.onAddScope(),
        },
    ];

    isLoadingScopesProfiles = false;
    isLoadingScopes = false;

    existingScopes: UserContextRoleScopeDTO[] = [];
    newScopes: UserContextRoleScopeDTO[] = [];
    contextRoleCd: string | undefined;
    roleGrantedFromLabel?: string;
    roleGrantedToLabel?: string;
    canCreateAccountsLabel?: string;
    canReceiveNotifLabel?: string;

    @Input()
    isNewUser = false;

    @Input()
    isNewRole = false;

    @Input()
    canShowPreferredLanguage = true;

    @Input()
    accountInfo: UserAccountInformationDTO = {};

    private _contextRoleO: UserContextRoleDTO = { userRoleContextId: -1 };
    private _contextRole: UserContextRoleDTO = { userRoleContextId: -1 };
    @Input()
    set contextRole(cr: UserContextRoleDTO | undefined) {
        this._contextRole = cr ?? { userRoleContextId: -1 };
        this._newScopeFields = [];
        this.existingScopes = [];
        this.newScopes = [];

        if (isAssigned(this._contextRole.information)) {
            this._contextRole.information!.preferredLanguageCd = this._contextRole.information?.preferredLanguageCd ?? this.accountInfo.preferredLanguageCd;
            this._contextRole.information!.canCreateAccounts = this._contextRole.information!.canCreateAccounts === true;
            this._contextRole.information!.canReceiveApprovalNotifications = this._contextRole.information!.canReceiveApprovalNotifications === true;
        } else {
            this._contextRole.information = {
                userRoleContextId: this._contextRole.userRoleContextId,
                preferredLanguageCd: this.accountInfo.preferredLanguageCd,
            };
        }

        this._contextRole.information!.preferredLanguageCd = this._contextRole.information!.preferredLanguageCd?.toLowerCase();

        this._contextRoleO = cloneDeep(this._contextRole);

        this._showAddScopeAction = false;

        this.readRoleDetails(() => {
            if (!this.isEmpty(this._contextRole.contextCd) && !this.isEmpty(this._contextRole.roleCd)) {
                this.readScopesOrProfiles(undefined, () => {
                    this.existingScopes = clone(this.newScopes);
                });
            }
        });

        this.emitOnChanged();
    }
    get contextRole(): UserContextRoleDTO {
        return this._contextRole;
    }

    private _newScopeFields: UserContextRoleScopeFieldDTO[] = [];
    private _newScopeFieldsTextC = false;
    get newScopeFields(): UserContextRoleScopeFieldDTO[] {
        return this._newScopeFields;
    }

    constructor(
        private readonly _dialogSrv: DialogService,
        private readonly _contextRoleSrv: ContextRoleService,
        translateSrv: TranslateService,
        toastr: ToastrService,
        globals: GlobalsService,
        metaSrv: MetaService,
        location: Location,
    ) {
        super(translateSrv, toastr, globals, metaSrv, location);

        this._translateKey = ComponentName;
    }

    private findHasProfile() {
        this._contextRole.information!.hasProfile = isAssigned(this._contextRole.scopeFields?.find((sf) => sf.fieldType?.toLowerCase() === 'switch'));
    }

    protected override fillReadMetaRequest(request: ReadMetaRequestDTO): void {
        request.contextCd = this._contextRole.contextCd;
        request.roleCd = this._contextRole.roleCd;
    }

    private get deletedScopes(): UserContextRoleScopeDTO[] {
        return getDeletedScopes(this.contextRole.scopeFields, this.existingScopes, this.newScopes);
    }

    private get hasDeletedScopes(): boolean {
        return this.deletedScopes.length > 0;
    }

    override get isLoadingInternal(): boolean {
        return super.isLoadingInternal || this.isLoadingScopesProfiles || this.isLoadingScopes || this.isLoadingScopesTable;
    }

    get canAddRoleAsApprover(): boolean {
        return this.accountInfo.canAddRoleAsApprover === true;
    }

    get canEditRoleAsApprover(): boolean {
        return this.contextRole.information?.canEditRoleAsApprover === true;
    }

    get canProfileRoleAsApprover(): boolean {
        return this.canEditRoleAsApprover || this.canAddRoleAsApprover;
    }

    get showRoleAsApprover(): boolean {
        return this.isNewUser || this.canProfileRoleAsApprover;
    }

    get showAddScopeAction(): boolean {
        return this._showAddScopeAction || this.newScopes.length < 1;
    }

    get newScopeItemActions(): ActionItemDef[] {
        const show = this.newScopeFields.filter((sf) => sf.fieldType?.toLowerCase() === 'text').length > 0;

        return show ? this._newScopeItemActions : [];
    }

    get newScopeFooterActions(): ActionDef[] {
        return this.showAddScopeAction ? this._newScopeFooterActions : [];
    }

    get existingScopesDeleteLabel(): string {
        if (this.accountInfo.isInternal === true) {
            return this.translate('internal.delete.header');
        }

        return this.translate('external.delete.header');
    }

    get existingScopesDeleteWarning(): string {
        if (this.accountInfo.isInternal === true) {
            return this.translate('internal.delete.warning');
        }

        return this.translate('external.delete.warning');
    }

    get newScopesAddLabel(): string {
        const withProfile = this.contextRole.information?.hasProfile === true ? 'Profile' : '';
        const req = this.hasRequiredFields ? ' *' : '';

        if (this.accountInfo.isInternal === true) {
            if (this.contextRole.information?.hasScope === true) {
                return this.translate(`internal.addScope.header${withProfile}`) + req;
            } else {
                return this.translate(`internal.add.header${withProfile}`) + req;
            }
        }

        if (this.contextRole.information?.hasScope === true) {
            return this.translate(`external.addScope.header${withProfile}`) + req;
        } else {
            return this.translate(`external.add.header${withProfile}`) + req;
        }
    }

    get newScopesWarning(): string {
        const res = this.chechScopesValid();

        if (res === 2) {
            if (this.accountInfo.isInternal === true) {
                return this.translate('internal.add.warning');
            }

            return this.translate('external.add.warning');
        } else if (res === 1) {
            if (this.accountInfo.isInternal === true) {
                return this.translate('internal.edit.warning');
            }

            return this.translate('external.edit.warning');
        }

        return '';
    }

    get newScopesAddButton(): string {
        if (this.accountInfo.isInternal === true) {
            return this.translate('internal.addScope.button');
        }

        return this.translate('external.addScope.button');
    }

    get scopesDeleteButton(): string {
        if (this.accountInfo.isInternal === true) {
            return this.translate('internal.delete.button');
        }

        return this.translate('external.delete.button');
    }

    get scopesDeleteUndoButton(): string {
        if (this.accountInfo.isInternal === true) {
            return this.translate('internal.delete.undoButton');
        }

        return this.translate('external.delete.undoButton');
    }

    get contextRoleCds(): OptionDTO[] {
        return this.metaOptions['availableContextRoles'] ?? [];
    }

    get contextRoleCdFilter(): boolean {
        return this.contextRoleCds.length > 15;
    }

    get languages(): OptionDTO[] {
        return this.metaOptions['language'] ?? [{ val: 'en', label: 'English' }];
    }

    get isScopesVisible(): boolean {
        return this.isContextRoleCdValid && this.newScopeFields.length > 0;
    }

    get isContextRoleValid(): boolean {
        return this.isNewRole ? !this.isEmpty(this.contextRoleCd) : true;
    }

    get isRoleGrantedDatesValid(): boolean {
        if (isAssigned(this._contextRole.information?.roleGrantedFromDate) && isAssigned(this._contextRole.information?.roleGrantedToDate)) {
            return this._contextRole.information?.roleGrantedFromDate! < this._contextRole.information?.roleGrantedToDate!;
        }

        return true;
    }

    get isPreferredLanguageValid(): boolean {
        return this.canShowPreferredLanguage && !this.isEmpty(this._contextRole.information?.preferredLanguageCd);
    }

    get atLeastOneApprovalIsRequired(): boolean {
        return this.isNewUser || this.isNewRole;
    }

    get canAddMultipleScopes(): boolean {
        return firstOrDefault(this.newScopeFields)?.canAddMultipleScopes === true;
    }

    get hasRequiredFields(): boolean {
        return isAssigned(this.newScopeFields.find((sf) => sf.isRequired === true));
    }

    private get requiredTextFields(): UserContextRoleScopeFieldDTO[] {
        return this.newScopeFields.filter((sf) => sf.fieldType?.toLowerCase() === 'text' && sf.isRequired === true);
    }

    private get textFields(): UserContextRoleScopeFieldDTO[] {
        return this.newScopeFields.filter((sf) => sf.fieldType?.toLowerCase() === 'text');
    }

    private get switchFields(): UserContextRoleScopeFieldDTO[] {
        return this.newScopeFields.filter((sf) => sf.fieldType?.toLowerCase() === 'switch');
    }

    private filterSwitches(
        switchFields: UserContextRoleScopeFieldDTO[],
        scopes: UserContextRoleScopeDTO[],
        withDenied?: boolean,
        checkSwitches?: boolean,
    ): UserContextRoleScopeDTO[] {
        if (switchFields.length === 0) {
            return [];
        }

        const iaValidSwitch = (s: UserContextRoleScopeDTO, sf: UserContextRoleScopeFieldDTO): boolean => {
            const sfknE = `${sf.fieldMapping}CanApprove` as AnyKey;
            const ena = (s as any)[sfknE] === true;

            if (isAssigned(withDenied)) {
                const sfknV = `${sf.fieldMapping}Value` as AnyKey;
                const val = (s as any)[sfknV]?.toUpperCase() ?? '';
                return ena && (val.startsWith('APPROVED') || (withDenied && val.startsWith('DENIED')));
            } else {
                return ena;
            }
        };

        const iaValidText = (s: UserContextRoleScopeDTO, sf: UserContextRoleScopeFieldDTO): boolean => {
            const sfknV = `${sf.fieldMapping}Value` as AnyKey;
            const val = (s as any)[sfknV];
            return !isEmpty(val);
        };

        const check = !isAssigned(checkSwitches) || checkSwitches === true;

        if (check) {
            return scopes.filter(
                (s) => s.isDeleted !== true && s.canEditProfileRowAction === true && isAssigned(switchFields.find((sf) => iaValidSwitch(s, sf))),
            );
        } else {
            return scopes.filter((s) => s.isDeleted !== true && switchFields.filter((sf) => iaValidText(s, sf)).length === switchFields.length);
        }
    }

    private chechScopesValid(): number {
        if (this.accountInfo.canAddRoleAsApprover !== true) {
            return 0;
        }

        const switchFields = this.switchFields;
        if (switchFields.length === 0 || this.newScopeFields.length === 0) {
            return 0;
        }

        const editableScopes = this.filterSwitches(switchFields, this.newScopes);
        if (editableScopes.length === 0) {
            return 0;
        }

        const hasRequiredFields = this.hasRequiredFields;
        const atLeastOneApprovalIsRequired = this.atLeastOneApprovalIsRequired;

        if (switchFields.length === 0) {
            if (hasRequiredFields) {
                return editableScopes.length > 0 || !this.showAddScopeAction || this.newScopes.length > 0 ? 0 : 1;
            }

            const min = atLeastOneApprovalIsRequired ? 1 : 0;

            return editableScopes.length >= min || this.newScopes.length > 0 ? 0 : 1;
        }

        const adScopes = this.filterSwitches(switchFields, editableScopes, true);
        const apScopes = this.isNewRole ? this.filterSwitches(switchFields, editableScopes, false).length >= 1 : true;

        const eqScopes = editableScopes.length === adScopes.length;

        if (atLeastOneApprovalIsRequired) {
            if (editableScopes.length > 0) {
                return apScopes ? (eqScopes ? 0 : 1) : 2;
            }

            return 1;
        } else if (this.canAddMultipleScopes) {
            if (editableScopes.length > 0) {
                return apScopes ? (eqScopes ? 0 : 1) : 2;
            }

            return 0;
        }

        const res = editableScopes.length === 1 && eqScopes;

        return res ? 0 : 1;
    }

    get isNewScopesValid(): boolean {
        return this.chechScopesValid() === 0;
    }

    get isRequiredValid(): boolean {
        if (this.hasRequiredFields) {
            const editableScopes = this.filterSwitches(this.requiredTextFields, this.newScopes, undefined, false);
            return editableScopes.length > 0;
        }

        return true;
    }

    override get isValid(): boolean {
        return (
            super.isValid &&
            this.isPreferredLanguageValid &&
            this.isContextRoleValid &&
            this.isRequiredValid &&
            this.isRoleGrantedDatesValid &&
            this.isNewScopesValid
        );
    }

    get hasScopesChanged(): boolean {
        if (this.hasDeletedScopes) {
            return true;
        }

        // let schanged = this.existingScopes.filter((s) => s.hasChanged === true || s.isDeleted === true);

        // if (schanged.length === 0) {
        let schanged = this.newScopes.filter((s) => s.hasChanged === true || s.isDeleted === true);
        // }

        return schanged.length > 0;
    }

    override get hasChanged(): boolean {
        if (super.hasChanged) {
            return true;
        }

        if (this.isNewRole && !this.isEmpty(this.contextRoleCd)) {
            return true;
        }

        if (
            this._contextRole.information?.roleGrantedFromDate !== this._contextRoleO.information?.roleGrantedFromDate ||
            this._contextRole.information?.roleGrantedToDate !== this._contextRoleO.information?.roleGrantedToDate
        ) {
            return true;
        }

        if (
            this.canCreateAccountsLabel &&
            (this.isNewUser || this.isNewRole || this._contextRole.information?.canCreateAccounts !== this._contextRoleO.information?.canCreateAccounts)
        ) {
            return true;
        }

        if (
            this.canReceiveNotifLabel &&
            (this.isNewUser ||
                this.isNewRole ||
                this._contextRole.information?.canReceiveApprovalNotifications !== this._contextRoleO.information?.canReceiveApprovalNotifications)
        ) {
            return true;
        }

        return this.hasScopesChanged || this._contextRole.information?.preferredLanguageCd !== this._contextRoleO.information?.preferredLanguageCd;
    }

    get isRoleInformationVisible(): boolean {
        return this.isContextRoleCdVisible || this.showRoleAsApprover || this.isScopesVisible;
    }

    get isContextRoleCdVisible(): boolean {
        return this.isNewRole;
    }

    get isContextRoleCdValid(): boolean {
        return this.isContextRoleCdVisible ? !this.isEmpty(this.contextRoleCd) && this.contextRoleCd !== ',' : true;
    }

    protected override get changedEvent(): ChangedEvent {
        const deletedScopes = this.deletedScopes;
        const $e: ContextRoleChangedEvent = {
            hasChanged: this.hasChanged,
            isValidated: this.isValid,
            contextCd: this._contextRole.contextCd ?? this._contextRole.information?.contextCd ?? '',
            roleCd: this._contextRole.roleCd ?? this._contextRole.information?.roleCd ?? '',
            preferedLanguageCd: this._contextRole.information?.preferredLanguageCd ?? '',
            grantedFrom: this._contextRole.information?.roleGrantedFromDate,
            grantedTo: this._contextRole.information?.roleGrantedToDate,
            scopeFields: this.newScopeFields,
            existingScopes: this.existingScopes,
            newScopes: this.newScopes,
            deletedScopes: deletedScopes,
            hasDeletedScopes: deletedScopes.length > 0,
        };

        return $e;
    }

    private translateScopes(scopes: UserContextRoleScopeDTO[], scopeFields: UserContextRoleScopeFieldDTO[]) {
        scopes.forEach((s) => {
            scopeFields
                .filter((sf) => sf.fieldType?.toLowerCase() === 'switch')
                .forEach((sf) => {
                    const sfvk = `${sf.fieldMapping}Value` as AnyKey;
                    const sflk = sf.fieldMapping as AnyKey;
                    const val = (((s as any)[sfvk] as string) ?? '').toLowerCase();

                    if (!this.isEmpty(val)) {
                        const trn = this.translateDirect(`profile.status.${val}`);

                        (s as any)[sflk] = trn.toUpperCase();
                    }
                });
        });
    }

    private readScopesProfiles(scopes: TypeScopeDTO[] | undefined, onFinish: (response: any) => void, onError: (message: string) => void) {
        if (this.isLoadingScopesProfiles) {
            return;
        }

        this.isLoadingScopesProfiles = true;
        this.emitOnLoading();

        clearAllInTypeScopes(scopes);

        const request: ReadContextRoleScopeProfilesRequestDTO = {
            userId: this.contextRole.information?.userId ?? this.accountInfo.userId,
            languageCd: this.translateSrv.currentLang,
            scope: scopes ?? [],
        };

        if (isAssigned(this._contextRole.contextCd)) {
            request.contextCd = this._contextRole.contextCd;
        }
        if (isAssigned(this._contextRole.roleCd)) {
            request.roleCd = this._contextRole.roleCd;
        }

        this._contextRoleSrv.readContextRoleScopeProfiles(request).subscribe({
            next: (response) => {
                this.isLoadingScopesProfiles = false;
                this.emitOnLoading();

                onFinish(response);
            },
            error: (error) => {
                this.isLoadingScopesProfiles = false;
                this.emitOnLoading();

                if (isAssigned(onError) && !isEmpty(error.message)) {
                    onError!(error.message!);
                }

                this.showErrorMessage(error);
            },
        });
    }

    private readScopes(scopes: TypeScopeDTO[] | undefined, onFinish: (response: any) => void, onError: () => void) {
        if (this.isLoadingScopes) {
            return;
        }

        this._newScopeFields = [];

        doReadContextRoleScope(
            this.toastr,
            this.globals,
            this._contextRoleSrv,
            (isLoading) => {
                this.isLoadingScopes = isLoading;
                this.emitOnLoading();
            },
            (response) => {
                onFinish(response);
            },
            () => onError(),
            this._contextRole,
            scopes,
        );
    }

    private isLoadingScopesTable = false;

    private readScopesOrProfiles(scopes?: TypeScopeDTO[], onFinish: () => void = () => {}, onError: () => void = () => {}) {
        this.isLoadingScopesTable = true;
        this.emitOnLoading();

        const doFinish = (response: any) => {
            const contextRoleTmp = cloneDeep(this._contextRole);
            const newScopeFieldsTmp = cloneDeep(this._newScopeFields);
            const newScopesTmp = cloneDeep(this.newScopes);

            updateFields(contextRoleTmp, newScopeFieldsTmp, response.scopeFields ?? [], newScopesTmp, response.scopes ?? [], (scope) => {
                if (isAssigned(scopes)) {
                    scope.hasChanged = true;
                }
            });

            this.translateScopes(newScopesTmp, contextRoleTmp.scopeFields ?? []);

            this._newScopeFields = contextRoleTmp.scopeFields ?? [];
            this._newScopeFieldsTextC = this._newScopeFields.filter((sf) => sf.fieldType?.toLowerCase() === 'text').length > 0;
            this.newScopes = newScopesTmp ?? [];
            this._contextRole.scopeFields = contextRoleTmp.scopeFields;
            this._contextRole.scopes = newScopesTmp ?? [];
            this._showAddScopeAction = firstOrDefault(contextRoleTmp.scopeFields)?.canAddMultipleScopes === true;

            this.isLoadingScopesTable = false;
            this.emitOnLoading();

            this.findHasProfile();
            this.emitOnChanged();

            onFinish();
        };

        const doError = () => {
            this.isLoadingScopesTable = false;
            this.emitOnLoading();

            onError();
        };

        if (this.canProfileRoleAsApprover) {
            this.readScopesProfiles(
                scopes,
                (res) => doFinish(res),
                () => doError(),
            );
        } else {
            this.readScopes(
                scopes,
                (res) => doFinish(res),
                () => doError(),
            );
        }
    }

    private readRoleDetails(onFinish: () => void) {
        if (isAssigned(this._contextRole.contextCd) && isAssigned(this._contextRole.roleCd)) {
            this.readMeta(
                (metaOptions) => {
                    const roleDetail = metaOptions['roleDetail'] ?? [];

                    this.roleGrantedFromLabel = firstOrDefault(roleDetail.filter((rd) => 'rolegrantedfrom' === rd.val.toLowerCase()))?.label;
                    this.roleGrantedToLabel = firstOrDefault(roleDetail.filter((rd) => 'rolegrantedto' === rd.val.toLowerCase()))?.label;
                    this.canCreateAccountsLabel = firstOrDefault(roleDetail.filter((rd) => 'cancreateaccounts' === rd.val.toLowerCase()))?.label;
                    this.canReceiveNotifLabel = firstOrDefault(roleDetail.filter((rd) => 'canreceivenoti' === rd.val.toLowerCase()))?.label;

                    onFinish();
                },
                ['roleDetail'],
            );
        } else {
            onFinish();
        }
    }

    onRemoveNewScope(scope: UserContextRoleScopeDTO) {
        const ind = this.newScopes.indexOf(scope);
        if (ind > -1) {
            this.newScopes.splice(ind, 1);
            this.emitOnChanged();
        }
    }

    onAddScope() {
        const existingScope = userContextRoleScopesToTypeScopes(this._contextRole, this._contextRole.scopes);

        showScopePicker(
            this._dialogSrv,
            this.accountInfo.isInternal === true ? ContextRoleScopePickerKind.Scope : ContextRoleScopePickerKind.Affiliation,
            existingScope,
            this.accountInfo,
            {
                userRoleContextId: this._contextRole.userRoleContextId > -1 ? this._contextRole.userRoleContextId : undefined,
                contextCd: this._contextRole.contextCd ?? this._contextRole.information?.contextCd ?? '',
                roleCd: this._contextRole.roleCd ?? this._contextRole.information?.roleCd ?? '',
                usage: 'scope',
            },
            (result: TypeScopeDTO[]) => {
                if (isAssigned(result) && (result?.length ?? 0) > 0) {
                    result?.forEach((s) => {
                        s.isActive = undefined;
                    });

                    this.readScopesOrProfiles(result, () => {});
                }
            },
        );
    }

    onContextRoleCdChanged($e: any) {
        if (this.isNewRole) {
            const crcd = this.contextRoleCd?.split(',');
            const l = crcd?.length ?? 0;

            this.roleGrantedFromLabel = undefined;
            this.roleGrantedToLabel = undefined;
            this.canCreateAccountsLabel = undefined;
            this.canReceiveNotifLabel = undefined;

            this.newScopes = [];
            this._newScopeFields = [];

            if (l >= 2) {
                if (l > 0) {
                    this._contextRole.contextCd = crcd![0];
                    this._contextRole.information!.contextCd = crcd![0];
                }
                if (l > 1) {
                    this._contextRole.roleCd = crcd![1];
                    this._contextRole.information!.roleCd = crcd![1];
                }

                this.onAnyChange($e);

                this.readRoleDetails(() => {
                    this._showAddScopeAction = false;
                    this.readScopesOrProfiles();
                });
            }
        }
    }

    onScopesChanged($e: any) {
        this.translateScopes(this.newScopes, this.newScopeFields);

        this.onAnyChange($e);
    }

    onDataSourceChanged($event: boolean) {
        this.isLoadingScopesTable = false;
        this.emitOnLoading();
    }
}
